/** @shell standard @header titleMode=inline */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, Alert, Platform, BackHandler, Linking } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';
import { ScreenHeader, useHeaderTopPadding, HeaderBackButton, HeaderIconButton } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { SvgIcon } from '../components/common/SvgIcon';
import { MainLoader } from '../components/load-main/MainLoader';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { StackScreenShell } from '../shared/theme/studio/stackScreenShell';
import { CheckoutGlobalService, invalidateOnCheckoutSuccess } from '../data/domains/checkout';
import { useTranslation } from '../shared/i18n';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useAuthStore } from '../shared/auth/store';
import { IssueReportState } from '../components/common/IssueReportState';
import { isWebRuntime } from '../shared/platform/runtimeTarget';

const LOADING_TIMEOUT_MS = 15000;
const LOG_TAG = '[PaymentWebView]';

function buildRedirectHtml(targetUrl: string, redirectingText: string): string {
  const forJs = targetUrl.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\r/g, '').replace(/\n/g, ' ');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body><p>${redirectingText}</p><script>window.location.replace('${forJs}');</script></body></html>`;
}

function createPaymentWebViewStyles(c: ThemeColors) {
  return StyleSheet.create({
    screen: {
      flex: 1,
    },
    webViewContainer: {
      flex: 1,
      minHeight: 200,
    },
    webView: {
      flex: 1,
      width: '100%',
    },
    loadingOverlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.backdrop,
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10,
    },
    loadingText: {
      marginTop: appTheme.spacing.md,
      fontSize: 14,
      color: c.textSecondary,
    },
    footer: {
      paddingVertical: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
      backgroundColor: c.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.border,
      alignItems: 'center',
    },
    secureIndicator: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    secureText: {
      marginStart: appTheme.spacing.xs,
      fontSize: 12,
      color: c.success,
      fontWeight: '500',
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: appTheme.spacing.xl,
    },
    errorTitle: {
      fontSize: 20,
      fontWeight: '600',
      color: c.text,
      marginTop: appTheme.spacing.lg,
    },
    errorMessage: {
      fontSize: 14,
      color: c.textSecondary,
      textAlign: 'center',
      marginTop: appTheme.spacing.sm,
    },
    retryButton: {
      marginTop: appTheme.spacing.xl,
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.xl,
      backgroundColor: c.primary,
      borderRadius: appTheme.borderRadius.md,
    },
    retryButtonText: {
      fontSize: 16,
      fontWeight: '600',
      color: c.onPrimary,
    },
  });
}

export const PaymentWebViewScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const on_back_exit = useExitOrGoBack();
  const route = useRoute();
  const query_client = useQueryClient();
  const customer_id = useAuthStore(s => s.user?.id);
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const webViewRef = useRef<WebView>(null);
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createPaymentWebViewStyles(themeColors), [themeColors]);
  const header_top_padding = useHeaderTopPadding();

  const params = (route.params as any) || {};
  const paymentUrl = (params.url || params.chargeUrl || '').trim();
  const orderId = params.orderId;
  const flow = params.flow;

  const [isLoading, setIsLoading] = useState(true);
  const [canGoBack, setCanGoBack] = useState(false);
  const [webViewReady, setWebViewReady] = useState(false);
  const [web_redirect_failed, set_web_redirect_failed] = useState(false);
  const is_web = isWebRuntime();

  useEffect(() => {
    if (!paymentUrl) {
      return;
    }
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, LOADING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [paymentUrl]);

  /**
   * PWA: payment gateways set X-Frame-Options / CSP frame-ancestors, so the iframe
   * WebView shim never shows the charge page. Leave the app shell with a top-level nav.
   */
  useEffect(() => {
    if (!is_web || !paymentUrl) {
      return;
    }
    setIsLoading(true);
    set_web_redirect_failed(false);
    try {
      if (typeof window !== 'undefined' && typeof window.location?.assign === 'function') {
        window.location.assign(paymentUrl);
        return;
      }
    } catch {
      // fall through to Linking
    }
    Linking.openURL(paymentUrl).catch(() => {
      set_web_redirect_failed(true);
      setIsLoading(false);
    });
  }, [is_web, paymentUrl]);

  const open_payment_in_browser = useCallback(() => {
    if (!paymentUrl) {
      return;
    }
    Linking.openURL(paymentUrl).catch(() => {
      set_web_redirect_failed(true);
      Alert.alert(t('common.error'), t('paymentWebView.failedToLoadPayment'));
    });
  }, [paymentUrl, t]);

  const handleClose = useCallback(() => {
    Alert.alert(t('paymentWebView.cancelPaymentTitle'), t('paymentWebView.cancelPaymentMessage'), [
      {
        text: t('common.no'),
        style: 'cancel',
      },
      {
        text: t('common.yes'),
        style: 'destructive',
        onPress: () => on_back_exit(),
      },
    ]);
  }, [on_back_exit, t]);

  useEffect(() => {
    if (Platform.OS !== 'android') {
      return undefined;
    }
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      handleClose();
      return true;
    });
    return () => sub.remove();
  }, [handleClose]);

  const handleNavigationStateChange = (navState: any) => {
    // console.log(LOG_TAG, 'navigationStateChange', {
    //   url: navState.url,
    //   loading: navState.loading,
    //   title: navState.title,
    //   canGoBack: navState.canGoBack,
    // });
    setCanGoBack(navState.canGoBack);

    const url = navState.url?.toLowerCase() || '';

    if (url.includes('payment/success') || url.includes('payment-success') || url.includes('order-confirm')) {
      handlePaymentSuccess();
    } else if (url.includes('payment/failed') || url.includes('payment-failed') || url.includes('order-failed')) {
      handlePaymentFailure();
    } else if (url.includes('payment/cancel') || url.includes('payment-cancel')) {
      handlePaymentCancel();
    }
  };

  const handlePaymentSuccess = () => {
    CheckoutGlobalService.setContext(null);
    if (flow !== 'order' && typeof customer_id === 'number') {
      void invalidateOnCheckoutSuccess(query_client, customer_id);
    }
    if (flow === 'order' && orderId != null) {
      navigation.reset({
        index: 1,
        routes: [
          { name: 'MainTabs' as never },
          {
            name: 'OrderConfirmed' as never,
            params: {
              orderId: String(orderId),
              orderNumber: String(orderId),
            },
          },
        ],
      });
    } else {
      Alert.alert(t('paymentWebView.paymentSuccessTitle'), t('paymentWebView.paymentSuccessMessage'), [
        {
          text: t('common.ok'),
          onPress: () => {
            navigation.reset({
              index: 0,
              routes: [{ name: 'MainTabs' as never }],
            });
          },
        },
      ]);
    }
  };

  const handlePaymentFailure = () => {
    if (flow === 'order') {
      navigation.reset({
        index: 1,
        routes: [
          { name: 'MainTabs' as never },
          {
            name: 'OrderFailed' as never,
            params: { orderId: orderId != null ? String(orderId) : undefined, errorMessage: t('paymentWebView.paymentFailedMessage') },
          },
        ],
      });
    } else {
      Alert.alert(t('paymentWebView.paymentFailedTitle'), t('paymentWebView.paymentFailedMessage'), [
        {
          text: t('paymentWebView.tryAgain'),
          onPress: () => webViewRef.current?.reload(),
        },
        {
          text: t('paymentWebView.cancel'),
          style: 'cancel',
          onPress: () => on_back_exit(),
        },
      ]);
    }
  };

  const handlePaymentCancel = () => {
    Alert.alert(t('paymentWebView.paymentCancelledTitle'), t('paymentWebView.paymentCancelledMessage'), [
      {
        text: t('paymentWebView.tryAgain'),
        onPress: () => webViewRef.current?.reload(),
      },
      {
        text: t('paymentWebView.goBack'),
        style: 'cancel',
        onPress: () => on_back_exit(),
      },
    ]);
  };

  const handleGoBack = () => {
    if (canGoBack) {
      webViewRef.current?.goBack();
    }
  };

  if (!paymentUrl) {
    return (
      <StackScreenShell colors={themeColors}>
        <View style={styles.screen}>
          <ScreenHeader titleMode="inline" title={t('paymentWebView.title')} onBack={on_back_exit} />
          <View style={[styles.errorContainer, { paddingTop: header_top_padding }]}>
            <IssueReportState
              issue_context={{
                page: t('paymentWebView.title'),
                issue: 'Payment URL missing or invalid',
                kind: 'validation',
              }}
              title={t('paymentWebView.paymentError')}
              message={t('paymentWebView.unableToLoadPayment')}
              onRetry={on_back_exit}
            />
          </View>
        </View>
      </StackScreenShell>
    );
  }

  if (is_web) {
    return (
      <StackScreenShell colors={themeColors}>
        <View style={styles.screen}>
          <ScreenHeader titleMode="inline" title={t('paymentWebView.title')} onBack={on_back_exit} />
          <View style={[styles.errorContainer, { paddingTop: header_top_padding }]}>
            <MainLoader size={48} color={themeColors.primary} />
            <Text style={[styles.loadingText, { marginTop: appTheme.spacing.lg }]}>
              {t('paymentWebView.redirecting')}
            </Text>
            {web_redirect_failed ? (
              <>
                <Text style={styles.errorMessage}>{t('paymentWebView.failedToLoadPayment')}</Text>
                <Clickable style={styles.retryButton} onPress={open_payment_in_browser} accessibilityRole="button">
                  <Text style={styles.retryButtonText}>{t('paymentWebView.tryAgain')}</Text>
                </Clickable>
              </>
            ) : (
              <Clickable style={styles.retryButton} onPress={open_payment_in_browser} accessibilityRole="button">
                <Text style={styles.retryButtonText}>{t('paymentWebView.tryAgain')}</Text>
              </Clickable>
            )}
          </View>
          <View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, appTheme.spacing.sm) }]}>
            <View style={styles.secureIndicator}>
              <SvgIcon name="lock" size={16} color={themeColors.success} />
              <Text style={styles.secureText}>{t('paymentWebView.securePayment')}</Text>
            </View>
          </View>
        </View>
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={themeColors}>
      <View style={styles.screen}>
        <ScreenHeader
          titleMode="inline"
          title={t('paymentWebView.title')}
          leftAction={
            <HeaderBackButton
              onPress={handleGoBack}
              disabled={!canGoBack}
              accessibilityLabel={t('common.back')}
              appearance="elevated"
              iconColor={canGoBack ? themeColors.text : themeColors.textLight}
            />
          }
          rightAction={
            <HeaderIconButton onPress={handleClose} accessibilityLabel={t('common.close')} appearance="elevated">
              <SvgIcon name="x" size={22} color={themeColors.text} />
            </HeaderIconButton>
          }
        />

        {isLoading && (
          <View style={styles.loadingOverlay} pointerEvents="none">
            <MainLoader size={48} color={themeColors.primary} />
            <Text style={styles.loadingText}>{t('paymentWebView.loadingPayment')}</Text>
          </View>
        )}

        <View
          style={[styles.webViewContainer, { paddingTop: header_top_padding }]}
          onLayout={() => {
            if (!webViewReady) {
              setWebViewReady(true);
            }
          }}>
          {webViewReady && (
            <WebView
              key={paymentUrl}
              ref={webViewRef}
              source={{ html: buildRedirectHtml(paymentUrl, t('paymentWebView.redirecting')) }}
              style={styles.webView}
              onNavigationStateChange={handleNavigationStateChange}
              onLoadStart={() => {
                // console.log(LOG_TAG, 'onLoadStart', { uri: paymentUrl });
                setIsLoading(true);
              }}
              onLoadEnd={e => {
                const { nativeEvent } = e;
                const loadedUrl = (nativeEvent?.url || '').toLowerCase();
                // console.log(LOG_TAG, 'onLoadEnd', {
                //   url: nativeEvent?.url,
                //   loading: nativeEvent?.loading,
                // });
                setIsLoading(false);
                if ((loadedUrl === 'about:blank' || loadedUrl === '') && paymentUrl) {
                  const escaped = paymentUrl.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
                  const script = `window.location.replace('${escaped}'); true;`;
                  // console.log(LOG_TAG, 'iOS about:blank fallback: injecting redirect');
                  setTimeout(() => webViewRef.current?.injectJavaScript(script), 100);
                }
              }}
              onError={syntheticEvent => {
                setIsLoading(false);
                const { nativeEvent } = syntheticEvent;
                console.warn(LOG_TAG, 'onError', {
                  description: nativeEvent?.description,
                  code: nativeEvent?.code,
                  domain: nativeEvent?.domain,
                  nativeEvent: JSON.stringify(nativeEvent),
                });
                Alert.alert(t('common.error'), t('paymentWebView.failedToLoadPayment'));
              }}
              onHttpError={syntheticEvent => {
                setIsLoading(false);
                const { nativeEvent } = syntheticEvent;
                console.warn(LOG_TAG, 'onHttpError', {
                  statusCode: nativeEvent?.statusCode,
                  description: nativeEvent?.description,
                  url: nativeEvent?.url,
                });
                if (nativeEvent.statusCode >= 400) {
                  Alert.alert(t('common.error'), t('paymentWebView.paymentPageError').replace('{{code}}', String(nativeEvent.statusCode)));
                }
              }}
              onShouldStartLoadWithRequest={_request => {
                return true;
              }}
              javaScriptEnabled={true}
              domStorageEnabled={true}
              startInLoadingState={true}
              scalesPageToFit={true}
              mixedContentMode="compatibility"
              originWhitelist={['https://*', 'http://*']}
              allowFileAccess={false}
              cacheEnabled={false}
              {...(Platform.OS === 'android' && {
                setSupportMultipleWindows: false,
              })}
            />
          )}
        </View>

        <View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, appTheme.spacing.sm) }]}>
          <View style={styles.secureIndicator}>
            <SvgIcon name="lock" size={16} color={themeColors.success} />
            <Text style={styles.secureText}>{t('paymentWebView.securePayment')}</Text>
          </View>
        </View>
      </View>
    </StackScreenShell>
  );
};

export default PaymentWebViewScreen;
